import React, { useEffect, useMemo, useState } from 'react';
import { apiGet } from '../lib/api';
import { Badge, riskToneFor } from '../lib/badge';
import Icon from '../components/icons';

const PAGE_SIZE = 10;
const BADGE_COLUMNS = new Set(['lifecycle', 'state', 'status', 'trust', 'review']);

export default function ListView({
  title,
  path,
  columns,
  mapRow = (row) => row,
  queryParam,
  subtitle,
  icon,
  actions,
  reloadKey,
  rowActions,
}) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    setErr('');
    apiGet(path)
      .then((data) => setRows(Array.isArray(data) ? data.map(mapRow) : []))
      .catch((e) => setErr(String(e)))
      .finally(() => setLoading(false));
  }, [path, reloadKey]);

  useEffect(() => {
    setPage(1);
  }, [filter, path]);

  const filtered = useMemo(() => {
    if (!queryParam || !filter) return rows;
    return rows.filter((row) =>
      String(row[queryParam] ?? '')
        .toLowerCase()
        .includes(filter.toLowerCase())
    );
  }, [rows, queryParam, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <main className="page">
      <div className="page__header page__header--split">
        <div className="page__header-row">
          {icon && (
            <span className="page__icon">
              <Icon name={icon} />
            </span>
          )}
          <div className="page__header-text">
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
            {!loading && !err && (
              <p className="page__meta">
                Showing {filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}–
                {Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
              </p>
            )}
          </div>
        </div>
        {actions && <div className="page__header-actions">{actions}</div>}
      </div>
      {queryParam && (
        <div className="search-bar">
          <Icon name="search" className="search-bar__icon" />
          <input
            className="field-input search-bar__input"
            placeholder={`Search by ${queryParam}`}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      )}
      {err && <div className="status status--error">{err}</div>}
      {loading ? (
        <TableSkeleton columns={columns.length} />
      ) : (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th key={col}>{col.replace(/_/g, ' ')}</th>
                  ))}
                  {rowActions ? <th>actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row, index) => (
                  <tr key={row.id ?? index}>
                    {columns.map((col) => (
                      <td key={col}>{renderCell(col, row[col])}</td>
                    ))}
                    {rowActions ? <td className="data-table__actions">{rowActions(row)}</td> : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length > PAGE_SIZE && (
            <div className="pagination">
              <button
                type="button"
                className="btn-secondary btn-small"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ← Previous
              </button>
              <span className="pagination__label">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className="btn-secondary btn-small"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next →
              </button>
            </div>
          )}
          {filtered.length === 0 && <EmptyState label="No records found." />}
        </>
      )}
    </main>
  );
}

function renderCell(col, value) {
  if (value == null || value === '') return <span className="cell-muted">—</span>;

  if (col === 'risk_tier') {
    return <Badge tone={riskToneFor(value)}>{`tier ${value}`}</Badge>;
  }

  if (BADGE_COLUMNS.has(col)) {
    return <Badge value={value} />;
  }

  if (typeof value === 'boolean') {
    return <Badge tone={value ? 'success' : 'muted'}>{value ? 'yes' : 'no'}</Badge>;
  }

  if (col === 'quarantined' && (value === true || value === false)) {
    return <Badge tone={value ? 'danger' : 'success'}>{value ? 'quarantined' : 'clear'}</Badge>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="cell-muted">—</span>;
    return (
      <span className="chip-row">
        {value.slice(0, 3).map((v, i) => (
          <span className="chip" key={i}>{String(v)}</span>
        ))}
        {value.length > 3 && <span className="chip chip--more">+{value.length - 3}</span>}
      </span>
    );
  }

  if (typeof value === 'object') return <span className="cell-muted">{JSON.stringify(value)}</span>;

  if (col === 'id' || /_id$/.test(col)) {
    return <span className="cell-strong">{String(value)}</span>;
  }

  return String(value);
}

function TableSkeleton({ columns }) {
  return (
    <div className="table-wrap">
      <table className="data-table data-table--skeleton">
        <tbody>
          {Array.from({ length: 5 }).map((_, r) => (
            <tr key={r}>
              {Array.from({ length: columns }).map((__, c) => (
                <td key={c}><span className="skeleton-bar" /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function EmptyState({ label }) {
  return (
    <div className="empty-state">
      <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M4 6h16M4 12h10M4 18h6" strokeLinecap="round" />
      </svg>
      <p>{label}</p>
    </div>
  );
}
